import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type AdminRole = "admin" | "sales" | "account_manager";

export function useAdmin() {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<AdminRole | null>(null);
  const [isAccountManager, setIsAccountManager] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkAdmin = async () => {
      if (!user) {
        if (isMounted) {
          setIsAdmin(false);
          setRole(null);
          setIsAccountManager(false);
          setLoading(false);
        }
        return;
      }

      // Fetch all role entries for this user (may have multiple)
      const { data, error } = await supabase
        .from("admin_users")
        .select("user_id, role")
        .eq("user_id", user.id);

      if (!isMounted) return;

      if (error) {
        console.error("[Auth] Failed to check admin status:", error);
        setIsAdmin(false);
        setRole(null);
        setIsAccountManager(false);
      } else if (data && data.length > 0) {
        setIsAdmin(true);
        const roles = data.map((d) => d.role as AdminRole);
        // Prefer admin > sales > account_manager for primary role
        const primaryRole = roles.includes("admin")
          ? "admin"
          : roles.includes("sales")
            ? "sales"
            : "account_manager";
        setRole(primaryRole);
        setIsAccountManager(roles.includes("account_manager"));
      } else {
        setIsAdmin(false);
        setRole(null);
        setIsAccountManager(false);
      }
      setLoading(false);
    };

    if (authLoading) return;
    checkAdmin();

    return () => {
      isMounted = false;
    };
  }, [user, authLoading]);

  return { isAdmin, role, isAccountManager, loading };
}
